import { useCallback } from 'react'

import emDash from '~/components/emdash'
import { type UnitSystem, useUserPrefs } from '~/hooks/useUserPrefs'

import type { LongDistanceUnit } from '../types/units'
import { type DistanceUnit, isDistanceUnit } from '../types/units'
import { useNumberFormatter } from './number'
import type { FormatterHook } from './types'
import { combineOptions } from './types'
import type { UnitsFormat } from './units'
import { formatUnits, formatUnitsAbbr } from './units'

const distanceFormats: UnitsFormat<DistanceUnit> = {
  mi: {
    long: 'miles',
    short: 'mi'
  },
  km: {
    long: 'kilometers',
    short: 'km'
  },
  m: {
    long: 'meters',
    short: 'm'
  }
}

export const formatDistanceUnits = formatUnits(distanceFormats)
export const formatDistanceUnitsAbbr = formatUnitsAbbr(distanceFormats)

const defaultOptions = {
  longForm: false,
  sigFigs: 2,
  units: 'mi',
  omitUnits: false,
  invalidStr: emDash
}

const exportOptions = { ...defaultOptions, omitUnits: true, invalidStr: '' }

export const useDistanceFormatter: FormatterHook = () => {
  const formatNumber = useNumberFormatter()

  return useCallback(
    (value, options) => {
      const {
        value: distance,
        longForm,
        sigFigs,
        units,
        omitUnits,
        invalidStr
      } = combineOptions({
        value,
        options,
        defaults: defaultOptions,
        exports: exportOptions
      })

      if (!isDistanceUnit(units)) return invalidStr
      if (typeof distance !== 'string' && typeof distance !== 'number') return invalidStr

      // Coerce to number
      const distanceNum = parseFloat(`${distance}`)
      if (isNaN(distanceNum)) return invalidStr

      const distanceStr = formatNumber(distanceNum, { maximumFractionDigits: sigFigs, export: options?.export })
      if (omitUnits) return distanceStr

      const formattedUnits = longForm ? formatDistanceUnits(units) : formatDistanceUnitsAbbr(units)
      return [distanceStr, formattedUnits].join(' ')
    },
    [formatNumber]
  )
}

export const formatUnitSystemToDistanceUnits = (unitSystem: UnitSystem): LongDistanceUnit => {
  switch (unitSystem) {
    case 'imperial':
    case 'uk':
      return 'mi'
    case 'metric':
      return 'km'
    default:
      return 'mi'
  }
}

export function usePreferredDistanceUnits() {
  const { preferredUnitSystem } = useUserPrefs()
  return formatUnitSystemToDistanceUnits(preferredUnitSystem)
}
